import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ImageCompressorService {
  /**
   * Mantiene tu API actual ✅
   * - maxWidth/maxHeight: 1100 por defecto
   * - quality: 0.6 por defecto
   *
   * NUEVO (opcional): maxBytes
   * Si lo pasas, intentará ajustar (calidad/resolución) para no pasar ese peso.
   */
  async compress(
    fileOrBlob: Blob,
    maxWidth = 1100,
    maxHeight = 1100,
    quality = 0.6,
    maxBytes?: number
  ): Promise<Blob> {
    return this.compressSmart(fileOrBlob, {
      maxWidth,
      maxHeight,
      quality,
      maxBytes,
      preferWebp: true,
    });
  }

  /**
   * Recomendación para FEED: thumbnail ligero (se carga volando)
   * ~120–250 KB típico
   */
  async compressForFeed(fileOrBlob: Blob): Promise<Blob> {
    return this.compressSmart(fileOrBlob, {
      maxWidth: 720,
      maxHeight: 720,
      quality: 0.62,
      maxBytes: 220 * 1024,
      preferWebp: true,
    });
  }

  /**
   * Recomendación para SUBIDA (vista detalle):
   * mejor calidad, sigue siendo razonable de peso
   * ~250–650 KB típico
   */
  async compressForUpload(fileOrBlob: Blob): Promise<Blob> {
    return this.compressSmart(fileOrBlob, {
      maxWidth: 1440,
      maxHeight: 1440,
      quality: 0.72,
      maxBytes: 650 * 1024,
      preferWebp: true,
    });
  }

  // -----------------------------
  // Core “smart”
  // -----------------------------
  private async compressSmart(
    fileOrBlob: Blob,
    opts: {
      maxWidth: number;
      maxHeight: number;
      quality: number;
      maxBytes?: number;
      preferWebp?: boolean;
      forceJpeg?: boolean;
    }
  ): Promise<Blob> {
    const maxW = opts.maxWidth ?? 1100;
    const maxH = opts.maxHeight ?? 1100;
    const startQuality = this.clamp(opts.quality ?? 0.6, 0.35, 0.92);
    const maxBytes = opts.maxBytes;
    const preferWebp = !!opts.preferWebp && !opts.forceJpeg;

    // 1) Bitmap (con orientación si está disponible)
    const imageBitmap = await this.createBitmap(fileOrBlob);

    try {
      // 2) Tamaño destino (sin escalar hacia arriba)
      const baseSize = this.getTargetSize(
        imageBitmap.width,
        imageBitmap.height,
        maxW,
        maxH
      );

      // Canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('No se pudo obtener el contexto 2D del canvas');

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // 3) Elegir formato (WebP si soportado)
      const mime = preferWebp && this.supportsWebp()
        ? 'image/webp'
        : 'image/jpeg';

      // 4) Primera pasada
      let curW = baseSize.width;
      let curH = baseSize.height;
      canvas.width = curW;
      canvas.height = curH;
      ctx.drawImage(imageBitmap, 0, 0, curW, curH);

      let q = startQuality;
      let out = await this.canvasToBlob(canvas, mime, q);

      // 5) Si hay objetivo de peso, ajustamos iterando
      if (maxBytes && out.size > maxBytes) {
        const minQ = 0.45;
        const minScale = 0.72; // no bajamos infinito la resolución
        const maxTries = 10;

        for (let i = 0; i < maxTries && out.size > maxBytes; i++) {
          // Primero bajamos calidad
          if (q > minQ) {
            q = Math.max(minQ, q - 0.07);
            out = await this.canvasToBlob(canvas, mime, q);
            continue;
          }

          // Si ya no podemos bajar más calidad, bajamos un poco resolución y re-dibujamos
          const nextW = Math.round(curW * 0.9);
          const nextH = Math.round(curH * 0.9);

          const scaleNow = nextW / baseSize.width;
          if (scaleNow < minScale) break;

          curW = nextW;
          curH = nextH;
          canvas.width = curW;
          canvas.height = curH;
          ctx.drawImage(imageBitmap, 0, 0, curW, curH);

          // Reset de calidad a un valor decente en el nuevo tamaño
          q = Math.max(minQ, startQuality - 0.1);
          out = await this.canvasToBlob(canvas, mime, q);
        }
      }

      // Limpieza canvas
      canvas.width = 1;
      canvas.height = 1;

      return out;
    } finally {
      // Liberar memoria del bitmap si el navegador lo soporta
      (imageBitmap as any)?.close?.();
    }
  }

  // -----------------------------
  // Helpers
  // -----------------------------
  private async createBitmap(blob: Blob): Promise<ImageBitmap> {
    // Algunos navegadores permiten respetar orientación EXIF aquí
    try {
      return await (createImageBitmap as any)(blob, {
        imageOrientation: 'from-image',
      });
    } catch {
      // Fallback
      return await createImageBitmap(blob);
    }
  }

  private canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality: number) {
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Fallo al comprimir la imagen'))),
        mime,
        quality
      );
    });
  }

  private getTargetSize(origW: number, origH: number, maxW: number, maxH: number) {
    const ratio = Math.min(maxW / origW, maxH / origH, 1);
    return {
      width: Math.max(1, Math.round(origW * ratio)),
      height: Math.max(1, Math.round(origH * ratio)),
    };
  }

  private clamp(n: number, min: number, max: number) {
    return Math.min(max, Math.max(min, n));
  }

  private supportsWebp(): boolean {
    // Cache simple: calculamos 1 vez
    if ((this as any)._webpSupport !== undefined) return (this as any)._webpSupport;

    try {
      const c = document.createElement('canvas');
      c.width = 1;
      c.height = 1;
      const data = c.toDataURL('image/webp');
      (this as any)._webpSupport = data.startsWith('data:image/webp');
      return (this as any)._webpSupport;
    } catch {
      (this as any)._webpSupport = false;
      return false;
    }
  }
}
